import type {
  MarketRateScope,
  RateGroupType,
  RateType,
} from "@intelligems/ig-types";

import {
  isSharedRateScope,
  marketRateScopeKey,
  marketRateScopeSetKey,
} from "./market-rate-scope";

/** Checkout names the carrier falls back to for a rate saved without one. */
export const STANDARD_SHIPPING_NAME = "Standard Shipping";
export const FREE_SHIPPING_NAME = "Free Shipping";
export const TIERED_SHIPPING_NAME = "Tiered Shipping";

/**
 * The prefix prepended to a shipping rate's `service_name` when serving a
 * preview/test checkout, so the merchant can tell test rates from live ones at
 * the real Shopify checkout. Applied identically to Intelligems carrier rates
 * and to custom shipping provider rates, so the end-user never has to label
 * preview rates themselves.
 */
export const PREVIEW_RATE_NAME_PREFIX = "Intelligems Preview: ";

export function withPreviewRateName(serviceName: string): string {
  return `${PREVIEW_RATE_NAME_PREFIX}${serviceName}`;
}

/**
 * Inverse of {@link withPreviewRateName}: strips the preview prefix if present,
 * else returns the name unchanged. The custom shipping provider broker strips
 * the (already-labeled) Intelligems base rates before feeding them to providers
 * as `inputRates`, so the provider chain runs on unlabeled rates and the broker
 * applies the label exactly once to the final array.
 */
export function stripPreviewRateName(serviceName: string): string {
  return serviceName.startsWith(PREVIEW_RATE_NAME_PREFIX)
    ? serviceName.slice(PREVIEW_RATE_NAME_PREFIX.length)
    : serviceName;
}

export interface ServiceNameSourceRate {
  name?: string | null;
  rateType: RateType;
}

export interface ServiceNameSourceGroup {
  id?: string;
  variationId: string;
  groupType: RateGroupType;
  scopes?: MarketRateScope[] | null;
  rates: ServiceNameSourceRate[];
}

/** The `service_name` the carrier callback serves a rate under (live). */
export function effectiveServiceName(
  rate: ServiceNameSourceRate,
  groupType: RateGroupType
): string {
  if (rate.name) {
    return rate.name;
  }
  if (groupType === "tieredByPriceOrWeight") {
    return TIERED_SHIPPING_NAME;
  }
  return rate.rateType === "threshold"
    ? FREE_SHIPPING_NAME
    : STANDARD_SHIPPING_NAME;
}

/**
 * The rates of a group the carrier callback can ever return, mirroring
 * `ShippingRateGroupProvider.getRate`: a flat group serves its first amount
 * rate, a threshold group its first threshold rate, a flat-with-threshold group
 * one of the two, and a tiered group any of its tiers.
 */
export function servableRates<Rate extends ServiceNameSourceRate>(group: {
  groupType: RateGroupType;
  rates: Rate[];
}): Rate[] {
  const first = (type: RateType) =>
    group.rates.filter((rate) => rate.rateType === type).slice(0, 1);
  switch (group.groupType) {
    case "flatRate":
      return first("amount");
    case "thresholdOnly":
      return first("threshold");
    case "flatRateWithThreshold":
      return [...first("threshold"), ...first("amount")];
    case "tieredByPriceOrWeight":
      return group.rates;
    default:
      return [];
  }
}

function servableNames(group: ServiceNameSourceGroup): string[] {
  return servableRates(group).map((rate) =>
    effectiveServiceName(rate, group.groupType)
  );
}

/**
 * The `service_name`s a scoped Markets placement must admit so every rate
 * serving `scopeKey` shows there, live and in preview. Shopify matches a
 * carrier rate group's `serviceConfiguration` entries against the rate's
 * `service_name` (not its `service_code`), so these names are the only thing
 * that tells one coverage's rates from another's.
 */
export function admittedServiceNames(
  groups: ServiceNameSourceGroup[],
  scopeKey: string
): string[] {
  const names = groups
    .filter(
      (group) =>
        isSharedRateScope(group.scopes) ||
        group.scopes!.some((scope) => marketRateScopeKey(scope) === scopeKey)
    )
    .flatMap(servableNames);
  return [
    ...new Set(names.flatMap((name) => [name, withPreviewRateName(name)])),
  ].sort();
}

export interface ServiceNameCollision {
  /** The checkout name as the merchant wrote it (first occurrence). */
  name: string;
  variationIds: string[];
  groupIds: string[];
}

const nameKey = (name: string) => name.trim().toLowerCase();

/**
 * Checkout names that rates with different "Applies to" coverage share, across
 * every test group of an experience. A scoped placement admits rates by name,
 * so a name admitted at one coverage would also show every other rate carrying
 * it there: rates sharing a name must serve exactly the same coverages. Names
 * compare trimmed and case-insensitively, which is never looser than Shopify.
 * With no scoped group nothing is filtered by name, so nothing collides.
 */
export function serviceNameCollisions(
  groups: ServiceNameSourceGroup[]
): ServiceNameCollision[] {
  if (groups.every((group) => isSharedRateScope(group.scopes))) {
    return [];
  }
  const byName = new Map<
    string,
    { name: string; scopeSets: Set<string>; groups: ServiceNameSourceGroup[] }
  >();
  for (const group of groups) {
    const scopeSet = marketRateScopeSetKey(group.scopes);
    for (const name of new Set(servableNames(group))) {
      const key = nameKey(name);
      const entry = byName.get(key) ?? {
        name,
        scopeSets: new Set<string>(),
        groups: [],
      };
      entry.scopeSets.add(scopeSet);
      entry.groups.push(group);
      byName.set(key, entry);
    }
  }
  return [...byName.values()]
    .filter((entry) => entry.scopeSets.size > 1)
    .map((entry) => ({
      name: entry.name,
      variationIds: [
        ...new Set(entry.groups.map((group) => group.variationId)),
      ],
      groupIds: entry.groups.flatMap((group) => (group.id ? [group.id] : [])),
    }));
}
